import * as yup from "yup";

import { fetchData } from "@utils/api";
import { APIMethods } from "@typeDefinitions";
import { ActionsDto, actionsSchema } from "@client/common";
import { MetadataSchema, Metadata } from "@typeDefinitions";
import { tagResourceSchema } from "@client/searchRecipesNew";
import { nutrientSchema } from "@client/common";

import qs from "qs";

export interface FetchPatientSharedCollectionsParams {
  patientId: string;
  page?: number;
  perPage?: number;
  query?: string;
  status?: string;
  tags?: number[];
  nutrients?: string[];
}

export const fetchPatientSharedCollections = async ({
  patientId,
  ...params
}: FetchPatientSharedCollectionsParams): Promise<FetchPatientSharedCollectionsResponse> => {
  const query = qs.stringify(params);

  const response = await fetchData<FetchPatientSharedCollectionsResponse>(
    `/dietitian/patients/${patientId}/shared-content-collections/search?${query}`,
    APIMethods.GET,
  );
  return fetchPatientSharedCollectionsSchema.validate(response);
};

const tagTranslationSchema = yup.object().shape({
  lang: yup.string().required(),
  name: yup.string().required(),
});

const tagSchema = yup.object().shape({
  id: yup.number().required(),
  system_id: yup.number().required(),
  clinic_id: yup.number().required(),
  tag_category_id: yup.number().required(),
  translations: yup.array().of(tagTranslationSchema).required(),
});

const macroSchema = yup.object().shape({
  id: yup.number().required(),
  value: yup.number().required(),
});

const mediaSchema = yup.object().shape({
  url: yup.string().required(),
});

const collectionSchema = yup.object().shape({
  id: yup.number().required(),
  clinicId: yup.number().nullable().default(null),
  name: yup.string().required(),
  nameEn: yup.string().nullable().default(null),
  hide: yup.boolean().required(),
  numberOfItems: yup.number().required(),
  tags: yup.array().of(tagResourceSchema).required(),
  macros: yup.array().of(nutrientSchema).required(),
  favorite: yup.array().of(yup.number().required()).required(),
  actions: actionsSchema.required(),
  media: mediaSchema.nullable().default(null),
});

const statusSchema = yup.object().shape({
  id: yup.number().required(),
});

const collectionItemSchema = yup.object().shape({
  collection: collectionSchema.defined(),
  status: statusSchema.required(),
  activeFrom: yup.string().required(),
});

const linksSchema = yup.object().shape({
  first: yup.string().required(),
  last: yup.string().required(),
  prev: yup.string().nullable().defined(),
  next: yup.string().nullable().defined(),
});

const fetchPatientSharedCollectionsSchema = yup.object().shape({
  data: yup.array().of(collectionItemSchema).required(),
  links: linksSchema.required(),
  meta: MetadataSchema,
});

interface Tag {
  id: number;
  systemId: number | null;
  clinicId: number | null;
  tagCategoryId: number;
  descriptionPl: string;
  descriptionEn: string;
}

interface Macro {
  id: number;
  value: number;
}

interface Media {
  url: string;
}

interface Collection {
  id: number;
  clinicId: number | null;
  name: string;
  nameEn: string | null;
  hide: boolean;
  numberOfItems: number;
  tags: Tag[];
  macros: Macro[];
  favorite: number[];
  actions: ActionsDto;
  media: Media | null;
}

interface Status {
  id: number;
}

interface CollectionItem {
  collection: Collection;
  status: Status;
  activeFrom: string;
}

interface Links {
  first: string;
  last: string;
  prev: string | null;
  next: string | null;
}

export interface FetchPatientSharedCollectionsResponse {
  data: CollectionItem[];
  links: Links;
  meta: Metadata;
}
